// Thin fetch wrapper for the Pages Functions API.
// Throws ApiError on non-2xx with the message from the server.

export class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

async function request(path, init = {}) {
  const isForm = init.body instanceof FormData;
  const resp = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      Accept: 'application/json',
      ...(init.body && !isForm ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers ?? {}),
    },
  });
  const text = await resp.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { /* non-json */ }
  if (!resp.ok) {
    throw new ApiError(data?.error ?? `Request failed (${resp.status})`, resp.status);
  }
  return data;
}

export const api = {
  // ── auth ──
  me: () => request('/api/auth/me'),
  login: (username, password) =>
    request('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () => request('/api/auth/logout', { method: 'POST' }),

  // ── first-time setup ──
  bootstrapStatus: () => request('/api/admin/bootstrap'),
  bootstrap: (payload) =>
    request('/api/admin/bootstrap', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // ── tournament ──
  // tournamentEvents was re-added (originally removed with the legacy
  // Search/TournamentEvents flow) to back the Top 8 snapshot event picker.
  // With a package, only that package's game's events when the tournament
  // has several games'.
  tournamentEvents: (slug, packageSlug = '') =>
    request(`/api/tournament/events?${new URLSearchParams({ slug, ...(packageSlug ? { package: packageSlug } : {}) })}`),
  eventSets: (eventId, page = 1, perPage = 25) =>
    request(
      `/api/tournament/sets?eventId=${encodeURIComponent(eventId)}&page=${page}&perPage=${perPage}`
    ),

  // ── overlay configs (Supabase-backed; was localStorage in v1) ──
  getOverlayConfig: () => request('/api/overlay-config'),
  putOverlayConfig: (kind, config) =>
    request(`/api/overlay-config/${encodeURIComponent(kind)}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
  deleteOverlayConfig: (kind) =>
    request(`/api/overlay-config/${encodeURIComponent(kind)}`, { method: 'DELETE' }),

  // ── per-package state (sponsors, branding, countdown) ──
  // Generic — operates on whatever package slug you pass.
  packageGetConfig: (slug) =>
    request(`/api/packages/${encodeURIComponent(slug)}/config`),
  packagePutConfig: (slug, config) =>
    request(`/api/packages/${encodeURIComponent(slug)}/config`, {
      method: 'PUT',
      body: JSON.stringify(config),
    }),
  packageUploadLogo: (slug, tier, file) => {
    const form = new FormData();
    form.append('tier', tier);
    form.append('file', file);
    form.append('name', file.name);
    return request(`/api/packages/${encodeURIComponent(slug)}/logo`, {
      method: 'POST',
      body: form,
    });
  },
  packageDeleteLogo: (slug, tier, key) =>
    request(
      `/api/packages/${encodeURIComponent(slug)}/logo?tier=${encodeURIComponent(tier)}&key=${encodeURIComponent(key)}`,
      { method: 'DELETE' }
    ),
  packageSnapshotTop8: (slug, eventId) =>
    request(`/api/packages/${encodeURIComponent(slug)}/top8`, {
      method: 'POST',
      body: JSON.stringify({ eventId }),
    }),
  packageClearTop8: (slug) =>
    request(`/api/packages/${encodeURIComponent(slug)}/top8`, { method: 'DELETE' }),
  // The merged state plus `live`, the set on the stream link (or the next
  // one queued) — the same read the overlays poll.
  packageGetState: (slug, { tournamentSlug = '', stream = '' } = {}) =>
    request(`/api/packages/${encodeURIComponent(slug)}/state?${new URLSearchParams({ slug: tournamentSlug, stream })}`),
  // The "Show on overlay" picker's sets: the stream queue's, plus a page of
  // an event's unfinished sets when eventId is given.
  packageOnStream: (slug, { eventId = '', page = 1 } = {}) =>
    request(`/api/packages/${encodeURIComponent(slug)}/on-stream?${new URLSearchParams({ eventId, page: String(page) })}`),
  // The pick on its own (the bracket page), '' for Auto.
  packageSetOnStream: (slug, setId) =>
    request(`/api/packages/${encodeURIComponent(slug)}/on-stream`, {
      method: 'POST',
      body: JSON.stringify({ setId }),
    }),
  // The bracket page: the tournament's events and stream, or an event's sets
  // (all of them, or only those changed since updatedAfter, unix seconds).
  packageBracket: (slug, { eventId = '', updatedAfter = null } = {}) =>
    request(`/api/packages/${encodeURIComponent(slug)}/bracket?${new URLSearchParams({
      eventId,
      ...(updatedAfter != null ? { updatedAfter: String(updatedAfter) } : {}),
    })}`),
  // Match control (start_match_enabled): the set as the card shows it, and
  // the actions (call, start, games, winner, dq, reset) with the user's token.
  packageMatch: (slug, setId) =>
    request(`/api/packages/${encodeURIComponent(slug)}/match-control?${new URLSearchParams({ setId })}`),
  packageMatchControl: (slug, body) =>
    request(`/api/packages/${encodeURIComponent(slug)}/match-control`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  // The OBS remote (functions/api/packages/[slug]/obs-remote.js): the relay
  // tab's heartbeat, or a remote's poll, cut or scanned pairing, by role.
  packageObsRemote: (slug, body) =>
    request(`/api/packages/${encodeURIComponent(slug)}/obs-remote`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  startggTokenStatus: () => request('/api/me/startgg-token'),
  // Admin: a user's start.gg token for running matches (write-only; the
  // answer is whether one is saved and whose it is).
  adminGetStartggToken: (userId) =>
    request(`/api/admin/users/${encodeURIComponent(userId)}/startgg-token`),
  adminSetStartggToken: (userId, token) =>
    request(`/api/admin/users/${encodeURIComponent(userId)}/startgg-token`, {
      method: 'PUT',
      body: JSON.stringify({ token }),
    }),
  adminRemoveStartggToken: (userId) =>
    request(`/api/admin/users/${encodeURIComponent(userId)}/startgg-token`, { method: 'DELETE' }),
  packageStampWinner: (slug, setId) =>
    request(`/api/packages/${encodeURIComponent(slug)}/winner`, {
      method: 'POST',
      body: JSON.stringify({ setId }),
    }),

  // ── licensing / activation ──
  packageBilling: (slug) =>
    request(`/api/packages/${encodeURIComponent(slug)}/billing`),
  packageRegisterTournament: (slug, input) =>
    request(`/api/packages/${encodeURIComponent(slug)}/tournament`, {
      method: 'POST',
      body: JSON.stringify({ input }),
    }),
  packageActivate: (slug, { userTournamentId, eventIds, streamName }) =>
    request(`/api/packages/${encodeURIComponent(slug)}/activate`, {
      method: 'POST',
      body: JSON.stringify({ userTournamentId, eventIds, streamName }),
    }),
  packageUndoActivation: (slug, activationId) =>
    request(`/api/packages/${encodeURIComponent(slug)}/undo-activation`, {
      method: 'POST',
      body: JSON.stringify({ activationId }),
    }),
  packageSetOnAir: (slug, userTournamentId) =>
    request(`/api/packages/${encodeURIComponent(slug)}/on-air`, {
      method: 'POST',
      body: JSON.stringify({ userTournamentId }),
    }),
  packageSetRunStream: (slug, userTournamentId, streamName) =>
    request(`/api/packages/${encodeURIComponent(slug)}/run-stream`, {
      method: 'POST',
      body: JSON.stringify({ userTournamentId, streamName }),
    }),
  packageQueueCheck: (slug) =>
    request(`/api/packages/${encodeURIComponent(slug)}/queue-check`),
  packageCheckout: (slug, quantity) =>
    request(`/api/packages/${encodeURIComponent(slug)}/checkout`, {
      method: 'POST',
      body: JSON.stringify({ quantity }),
    }),
  orderStatus: (orderId, trackId) =>
    request(`/api/orders/${encodeURIComponent(orderId)}${trackId ? `?track=${encodeURIComponent(trackId)}` : ''}`),
  adminAdjustCredits: (userId, { packageId, delta, reason }) =>
    request(`/api/admin/users/${encodeURIComponent(userId)}/credits`, {
      method: 'POST',
      body: JSON.stringify({ packageId, delta, reason }),
    }),
  adminResetCredits: (userId, { packageId, setTo, reason }) =>
    request(`/api/admin/users/${encodeURIComponent(userId)}/credits`, {
      method: 'POST',
      body: JSON.stringify({ packageId, setTo, reason }),
    }),
  adminExtendRun: (runId, days) =>
    request(`/api/admin/runs/${encodeURIComponent(runId)}/extend`, {
      method: 'POST',
      body: JSON.stringify({ days }),
    }),

  // ── me (signed-in user's package access) ──
  myPackages: () => request('/api/me/packages'),

  // Full catalog of packages (no grants) — for ghost cards on the dashboard.
  packagesCatalog: () => request('/api/packages/catalog'),

  // ── access requests (per-overlay grant queue) ──
  // Three-state view of every overlay (granted / pending / available) for
  // the signed-in user — backs the /access/<slug> page.
  accessState: () => request('/api/me/access-state'),
  // Batch-create pending requests. Already-granted and already-pending IDs
  // are silently skipped server-side; the response breaks them down so the
  // UI can summarise.
  requestAccess: (overlayIds) =>
    request('/api/me/access-requests', {
      method: 'POST',
      body: JSON.stringify({ overlayIds }),
    }),
  // Admin queue + decision endpoints.
  adminListAccessRequests: (status = 'pending') =>
    request(`/api/admin/access-requests?status=${encodeURIComponent(status)}`),
  adminDecideAccessRequest: (id, decision, notes) =>
    request(`/api/admin/access-requests/${encodeURIComponent(id)}/decide`, {
      method: 'POST',
      body: JSON.stringify({ decision, notes: notes ?? null }),
    }),

  // ── design requests (custom overlay build queue) ──
  // Different lifecycle from access-requests: review → in_progress →
  // fulfilled (or → denied). Fulfillment auto-grants the requester.
  listMyDesignRequests: () => request('/api/me/design-requests'),
  createDesignRequest: ({ packageSlug, baseOverlaySlug, requestedName, description, proposedVariables }) =>
    request('/api/me/design-requests', {
      method: 'POST',
      body: JSON.stringify({
        packageSlug,
        baseOverlaySlug: baseOverlaySlug ?? null,
        requestedName,
        description,
        proposedVariables: proposedVariables ?? [],
      }),
    }),
  adminListDesignRequests: (status = 'pending') =>
    request(`/api/admin/design-requests?status=${encodeURIComponent(status)}`),
  adminTransitionDesignRequest: (id, { status, fulfilledOverlayId, notes }) =>
    request(`/api/admin/design-requests/${encodeURIComponent(id)}/transition`, {
      method: 'POST',
      body: JSON.stringify({
        status,
        fulfilledOverlayId: fulfilledOverlayId ?? null,
        notes: notes ?? null,
      }),
    }),

  // ── admin (role: admin only) ──
  // Pass `forUserId` to scope private overlays to that user. Without it,
  // only shared overlays are returned (right answer for UserCreate, where
  // the target user doesn't exist yet).
  adminListPackages: (forUserId) =>
    request(
      forUserId
        ? `/api/admin/packages?for_user_id=${encodeURIComponent(forUserId)}`
        : '/api/admin/packages'
    ),
  adminListUsers: () => request('/api/admin/users'),

  // ── admin: per-package art overrides (card art, console banner) ──
  adminListPackageArt: () => request('/api/admin/package-art'),
  adminUploadPackageArt: (slug, kind, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return request(
      `/api/admin/package-art/${encodeURIComponent(slug)}/${encodeURIComponent(kind)}`,
      { method: 'POST', body: fd },
    );
  },
  adminResetPackageArt: (slug, kind) =>
    request(
      `/api/admin/package-art/${encodeURIComponent(slug)}/${encodeURIComponent(kind)}`,
      { method: 'DELETE' },
    ),
  // A screen's live layout overrides (functions/_lib/layouts.js): the fields
  // with their design defaults, and the stored values.
  adminGetLayout: (pkg, screen) => request(`/api/admin/layouts/${encodeURIComponent(pkg)}/${encodeURIComponent(screen)}`),
  adminPutLayout: (pkg, screen, values) =>
    request(`/api/admin/layouts/${encodeURIComponent(pkg)}/${encodeURIComponent(screen)}`, {
      method: 'PUT',
      body: JSON.stringify({ values }),
    }),
  // Winner-overlay per-character fit map (art/global/winner-char-fit.json).
  adminGetWinnerCharFit: () => request('/api/admin/winner-char-fit'),
  adminPutWinnerCharFit: (map) =>
    request('/api/admin/winner-char-fit', {
      method: 'PUT',
      body: JSON.stringify({ map }),
    }),
  // Top 8 (landscape) per-character fit map (art/global/top8-char-fit.json).
  adminGetTop8CharFit: () => request('/api/admin/top8-char-fit'),
  adminPutTop8CharFit: (map) =>
    request('/api/admin/top8-char-fit', {
      method: 'PUT',
      body: JSON.stringify({ map }),
    }),
  // Rules text on the public registration page (kec-register); the page
  // reads the public /api/registration/<slug> copy on every load.
  adminGetRegistrationSettings: () => request('/api/admin/registration-settings'),
  adminPutRegistrationSettings: ({ generalRules, gameRules, visibility }) =>
    request('/api/admin/registration-settings', {
      method: 'PUT',
      body: JSON.stringify({ generalRules, gameRules, visibility }),
    }),

  // ── Phase 2: per-grant access tokens + per-user HTML uploads ──
  adminListGrants: () => request('/api/admin/grants'),
  adminUploadGrantHtml: (accessToken, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return request(`/api/admin/grants/${encodeURIComponent(accessToken)}/upload`, {
      method: 'POST',
      body: fd,
    });
  },
  // Server-side default-applier — fetches /overlay/<slug>.html in the
  // function (not the browser) and writes it to R2. Sidesteps the dev-only
  // browser-cache poisoning where /overlay/*.html serves a stale SPA shell.
  adminMarkGrantCustom: (accessToken) =>
    request(`/api/admin/grants/${encodeURIComponent(accessToken)}/mark-custom`, {
      method: 'POST',
    }),
  adminApplyDefaultGrantHtml: (accessToken) =>
    request(`/api/admin/grants/${encodeURIComponent(accessToken)}/use-default`, {
      method: 'POST',
    }),
  adminGetUser: (id) =>
    request(`/api/admin/users/${encodeURIComponent(id)}`),
  adminCreateUser: (payload) =>
    request('/api/admin/users', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  adminUpdateUser: (id, patch) =>
    request(`/api/admin/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  adminDeleteUser: (id) =>
    request(`/api/admin/users/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  adminResetPassword: (id, newPassword) =>
    request(`/api/admin/users/${encodeURIComponent(id)}/password`, {
      method: 'PATCH',
      body: JSON.stringify({ newPassword }),
    }),
  adminSetUserOverlays: (id, overlayIds) =>
    request(`/api/admin/users/${encodeURIComponent(id)}/overlays`, {
      method: 'PUT',
      body: JSON.stringify({ overlayIds }),
    }),
  adminRotateUserKey: (id) =>
    request(`/api/admin/users/${encodeURIComponent(id)}/rotate-key`, {
      method: 'POST',
    }),

  // ── admin: audit log (read-only activity feed) ──
  // `before` is the keyset cursor (createdAt of the last row seen); omit it
  // for the first page. `category` is an optional action-prefix filter.
  adminListAuditLog: ({ category, before, limit } = {}) => {
    const qs = new URLSearchParams();
    if (category) qs.set('category', category);
    if (before) qs.set('before', before);
    if (limit) qs.set('limit', String(limit));
    const q = qs.toString();
    return request(`/api/admin/audit-log${q ? `?${q}` : ''}`);
  },
};
